import { useEffect, useRef } from 'react';
import type {
  CSSProperties,
  FC,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
} from 'react';

import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import { importFetchedStatus } from '@/mastodon/actions/importer';
import { apiViewStatus } from '@/mastodon/api/statuses';
import { useVisibility } from '@/mastodon/hooks/useVisibility';
import { me } from '@/mastodon/initial_state';
import type { Account, AccountShapeFull } from '@/mastodon/models/account';
import { selectAccountStatus } from '@/mastodon/selectors/statuses';
import { useAppDispatch, useAppSelector } from '@/mastodon/store';
import CircleGapIcon from '@/material-icons/400-24px/circle_gap.svg?react';
import GroupsIcon from '@/material-icons/400-24px/groups.svg?react';
import HourglassIcon from '@/material-icons/400-24px/hourglass.svg?react';
import ListAltIcon from '@/material-icons/400-24px/list_alt.svg?react';
import EyeIcon from '@/material-icons/400-24px/visibility.svg?react';

import { Avatar } from '../avatar';
import { AvatarOverlay } from '../avatar_overlay';
import type { DisplayNameProps } from '../display_name';
import { LinkedDisplayName } from '../display_name';
import { Icon } from '../icon';
import { RelativeTimestamp } from '../relative_timestamp';
import { VisibilityIcon } from '../visibility_icon';

export interface StatusHeaderProps {
  statusId: string;
  account?: Account | AccountShapeFull;
  avatarSize?: number;
  contentBeforeDate?: ReactNode;
  contentAfterDate?: ReactNode;
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
  displayNameProps?: DisplayNameProps;
  onHeaderClick?: MouseEventHandler<HTMLDivElement>;
  className?: string;
  featured?: boolean;
}

export type StatusHeaderRenderFn = (args: StatusHeaderProps) => ReactNode;

export const StatusHeader: FC<StatusHeaderProps> = ({
  statusId,
  account,
  className,
  avatarSize = 48,
  wrapperProps,
  contentBeforeDate,
  contentAfterDate,
  onHeaderClick,
}) => {
  const status = useAppSelector((state) =>
    selectAccountStatus(state, statusId),
  );
  const { pathname } = useLocation();
  const { isIntersecting, observedRef } = useVisibility();
  const viewRecordedRef = useRef(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isIntersecting || viewRecordedRef.current || !status) {
      return;
    }
    viewRecordedRef.current = true;
    // The response carries the fresh views_count -- import it back so the
    // count updates immediately rather than only after a future re-fetch.
    apiViewStatus(status.id)
      .then((data) => {
        dispatch(importFetchedStatus(data));
        return undefined;
      })
      .catch(() => undefined);
  }, [isIntersecting, status, dispatch]);

  if (!status) {
    return null;
  }

  // Don't repeat the group breadcrumb on posts shown within that same group's
  // own timeline — matches `/group/:id`, `/group/:id/media`, `/deck/group/:id`…
  const viewingGroupId = /(?:^|\/)group\/([^/]+)/.exec(pathname)?.[1];
  const showGroupBreadcrumb =
    !!status.group_id &&
    !!status.group_name &&
    status.group_id !== viewingGroupId;
  const showAudienceBreadcrumb = !!status.audience_list_id;
  const showListBreadcrumb = !!status.list_id;
  const statusAccount = status.account;
  // Only the author gets to see which audience list/List a limited post
  // targeted — everyone else (including members of it) just sees the icon,
  // so a viewer can't learn the names/existence of other private audiences
  // the author has set up.
  const isAuthor = !!me && me === statusAccount.id;
  const showAudienceName = showAudienceBreadcrumb && isAuthor;
  const showListTitle = showListBreadcrumb && isAuthor;
  const editedAt = status.edited_at;
  const expiresAt = status.expires_at;

  return (
    /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <div
      ref={observedRef}
      onClick={onHeaderClick}
      onAuxClick={onHeaderClick}
      {...wrapperProps}
      className={classNames('status__info', className)}
      style={{ '--status-avatar-size': `${avatarSize}px` } as CSSProperties}
      /* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    >
      <div className='status__info__main'>
        <StatusDisplayName
          statusAccount={statusAccount}
          friendAccount={account}
          avatarSize={avatarSize}
        />

        <div className='status__info__meta'>
          {contentBeforeDate}

          <Link
            to={`/@${statusAccount.acct}/${status.id}`}
            className='status__relative-time'
          >
            <span
              className='status__visibility-icon'
              {...(isAuthor && {
                'data-hover-card-status-visibility': status.id,
              })}
            >
              <VisibilityIcon visibility={status.visibility} />
            </span>
            <RelativeTimestamp timestamp={status.created_at} />
            {editedAt && <StatusEditedAt editedAt={editedAt} />}
            {expiresAt && <StatusExpiresAt expiresAt={expiresAt} />}
          </Link>

          <span className='status__views-count'>
            <Icon id='visibility' icon={EyeIcon} />
            <FormattedMessage
              id='status.views_count'
              defaultMessage='{count, plural, one {# view} other {# views}}'
              values={{ count: status.views_count }}
            />
          </span>

          {showGroupBreadcrumb && (
            <Link
              to={`/group/${status.group_id}`}
              className='status__display-name__group'
            >
              <Icon id='group' icon={GroupsIcon} />
              {status.group_name}
            </Link>
          )}

          {showAudienceBreadcrumb && (
            <span className='status__display-name__group'>
              <Icon id='circle_gap' icon={CircleGapIcon} noFill />
              {showAudienceName && status.audience_list_name}
            </span>
          )}

          {showListBreadcrumb && (
            <span className='status__display-name__group'>
              <Icon id='list_alt' icon={ListAltIcon} />
              {showListTitle && status.list_title}
            </span>
          )}
        </div>
      </div>

      {contentAfterDate}
    </div>
  );
};

const editMessage = defineMessage({
  id: 'status.edited',
  defaultMessage: 'Edited {date}',
});

const expiresMessage = defineMessage({
  id: 'status.expires',
  defaultMessage: 'Expires {date}',
});

const StatusExpiresAt: FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const intl = useIntl();
  return (
    <span
      className='status__relative-time__expiry'
      title={intl.formatMessage(expiresMessage, {
        date: intl.formatDate(expiresAt, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
      })}
    >
      <Icon id='hourglass' icon={HourglassIcon} />
    </span>
  );
};

const StatusEditedAt: FC<{ editedAt: string }> = ({ editedAt }) => {
  const intl = useIntl();
  return (
    <abbr
      title={intl.formatMessage(editMessage, {
        date: intl.formatDate(editedAt, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
      })}
    >
      {' '}
      *
    </abbr>
  );
};

const StatusDisplayName: FC<{
  statusAccount?: AccountShapeFull;
  friendAccount?: Account | AccountShapeFull;
  avatarSize: number;
}> = ({ statusAccount, friendAccount, avatarSize }) => {
  const AccountComponent = friendAccount ? AvatarOverlay : Avatar;
  return (
    <div className='status__display-name'>
      <LinkedDisplayName
        displayProps={{ account: statusAccount }}
        className='status__display-name__link'
      >
        <div className='status__avatar'>
          <AccountComponent
            account={statusAccount}
            friend={friendAccount}
            size={avatarSize}
          />
        </div>
      </LinkedDisplayName>
    </div>
  );
};
